// P3-019: Floating damage number that pops up and fades out
// Normal: white text, Crit: orange + larger + shake

import Phaser from 'phaser';
import { GAME_DPR } from '@/shared/game/main';

export interface DamageNumberConfig {
  x: number;
  y: number;
  amount: number;
  isCrit: boolean;
}

export class DamageNumber extends Phaser.GameObjects.Text {
  constructor(scene: Phaser.Scene, config: DamageNumberConfig) {
    const { x, y, amount, isCrit } = config;

    const fontSize = isCrit ? '28px' : '20px';
    const color = isCrit ? '#ff8800' : '#ffffff';

    super(scene, x, y, String(Math.round(amount)), {
      fontFamily: 'JetBrains Mono, Consolas, monospace',
      fontSize,
      color,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: isCrit ? 4 : 3,
      resolution: GAME_DPR,
    });

    this.setOrigin(0.5, 0.5);
    this.setDepth(100);
    scene.add.existing(this);

    this.playAnimation(isCrit);
  }

  private playAnimation(isCrit: boolean): void {
    const floatDistance = isCrit ? -60 : -40;
    const duration = isCrit ? 1000 : 800;

    // Scale pop-in
    this.setScale(0.3);
    this.scene.tweens.add({
      targets: this,
      scaleX: isCrit ? 1.3 : 1.0,
      scaleY: isCrit ? 1.3 : 1.0,
      duration: 150,
      ease: 'Back.easeOut',
    });

    // Float upward and fade out
    this.scene.tweens.add({
      targets: this,
      y: this.y + floatDistance,
      alpha: 0,
      duration,
      delay: 150,
      ease: 'Sine.easeIn',
      onComplete: () => {
        this.destroy();
      },
    });

    // Crit shake effect
    if (isCrit) {
      this.scene.tweens.add({
        targets: this,
        x: { from: this.x - 3, to: this.x + 3 },
        duration: 50,
        repeat: 4,
        yoyo: true,
        ease: 'Sine.easeInOut',
      });
    }
  }
}

/** Factory function: spawn a floating damage number */
export function spawnDamageNumber(
  scene: Phaser.Scene,
  config: DamageNumberConfig,
): DamageNumber {
  return new DamageNumber(scene, config);
}
